import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useLoaderData } from "react-router-dom";
import colors from "../../styles/colors";
import CommonWrapper from "../../components/ui_components/CommonComponentsStructureCss";
import { apiCreateTypeMatchV2, apiGetTypeMatchesGroupedByType, apiGetTypeMatchTypesV2 } from "../../api/matchApi";
import CreateTypeMatchModale from "../../components/admin/TypeMatchHandleFolder/CreateTypeMatchModale";

const Container = styled.div`
    width: 80%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-top: 20px;

    & h1.admin-type-match-handle-title {
        font-size: 24px;
        font-weight: 600;
        text-align: center;
        margin-bottom: 10px;
    }

    & p.admin-type-match-handle-description {
        font-size: 16px;
        font-weight: 400;
        text-align: center;
        margin-bottom: 10px;
    }
`;

const DivForButtonAggiungiTypeMatch = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 20px 0;

    & button.btn-handle-aggiungi-type-match {
        width: 300px;
        padding: 10px;
        border-radius: 5px;
        border: 1px solid white;
        background-color: ${colors.bgColorBlack2};
        color: white;
        font-size: 20px;
        text-transform: uppercase;
        cursor: pointer;
        transition: transform 0.2s ease-in-out, background-color 0.2s ease-in-out;
        
        &:hover {
            transform: scale(1.05);
            background-color: ${colors.bgColorBlack};
        }
    }
`;

const GroupsContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 40px;
`;

const GroupBox = styled.div`
    width: 100%;
    background-color: ${colors.bgColorBlack2};
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    overflow: hidden;
`;

const GroupHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 14px;
    background-color: ${colors.bgColorBlack};
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

    & h2 {
        margin: 0;
        font-size: 18px;
        font-weight: 700;
        color: gold;
        letter-spacing: 1px;
        text-transform: uppercase;
    }

    & span {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.7);
    }
`;

const GroupBody = styled.div`
    display: flex;
    flex-direction: column;
`;

const TypeMatchRow = styled.div`
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);

    &:last-child {
        border-bottom: none;
    }
`;

const TypeMatchName = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: white;
`;

const TypeMatchMeta = styled.div`
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
`;

const InfoBox = styled.div`
    width: 100%;
    background-color: ${colors.bgColorBlack2};
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 14px;
    margin-top: 10px;
    color: rgba(255, 255, 255, 0.85);
    text-align: center;
`;

function TypeMatchHandle() {
    const loaderData = useLoaderData() as any;

    const initialGrouped = useMemo(() => {
        // Loader può restituire response oppure direttamente errore
        if (!loaderData) return null;
        if (typeof loaderData === "string") return null;
        return loaderData.data || loaderData;
    }, [loaderData]);

    const [typeMatchesGrouped, setTypeMatchesGrouped] = useState<any>(initialGrouped);
    const [typeMatchTypes, setTypeMatchTypes] = useState<any[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);

    useEffect(() => {
        const controller = new AbortController();

        const loadAllTypes = async () => {
            setIsLoadingTypes(true);
            try {
                const response = await apiGetTypeMatchTypesV2() as any;
                if (controller.signal.aborted) return;
                if (response?.error) {
                    console.error("Errore nel caricare i tipi match:", response);
                    return;
                }

                const data = response?.data ?? response;
                const normalized = Array.isArray(data)
                    ? data.map((item) => {
                        if (typeof item === "string") {
                            return { correct_key_name: item, translate_key_name: item };
                        }
                        return item;
                    })
                    : [];
                setTypeMatchTypes(normalized);
            } catch (error: unknown) {
                if (error instanceof Error && error.name === 'AbortError') return;
                console.error("Errore nel caricare i tipi match:", error);
            } finally {
                if (!controller.signal.aborted) setIsLoadingTypes(false);
            }
        };

        loadAllTypes();

        return () => {
            controller.abort();
        };
    }, []);

    const groupedEntries = useMemo(() => {
        if (!typeMatchesGrouped || typeof typeMatchesGrouped !== "object" || Array.isArray(typeMatchesGrouped)) return [];
        return Object.entries(typeMatchesGrouped)
            .map(([groupName, list]) => [groupName, Array.isArray(list) ? list : []])
            .sort(([a], [b]) => (a as string).localeCompare(b as string, "it"));
    }, [typeMatchesGrouped]);

    const fetchAllTypeMatches = async () => {
        setIsLoading(true);
        try {
            const response = await apiGetTypeMatchesGroupedByType() as any;
            if (response.error) {
                console.error("Errore nel caricare i type matches:", response);
                return;
            }
            const data = response.data || response;
            setTypeMatchesGrouped(data);
        } catch (error) {
            console.error("Errore nel caricare i type matches:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateTypeMatch = async ({ name, type_match_type, num_teams, min_num_wrestler, max_num_wrestlers }: any) => {
        const payload = { name, type_match_type, num_teams, min_num_wrestler, max_num_wrestlers };
        const response = await apiCreateTypeMatchV2(payload as any) as any;

        // Pattern API: può tornare {error}, {errors}, oppure dati (eventualmente in data)
        if (response?.errors) {
            const firstErrorKey = Object.keys(response.errors)[0];
            const firstErrorValue = response.errors[firstErrorKey];
            const message = Array.isArray(firstErrorValue) ? firstErrorValue[0] : (firstErrorValue || "Errore di validazione");
            throw new Error(message);
        }
        if (response?.error) {
            throw new Error(response.error);
        }

        // Refresh lista dopo creazione
        await fetchAllTypeMatches();

        return response;
    };

    return (
        <CommonWrapper className="admin-type-match-handle-common-wrapper">
            <Container className="admin-type-match-handle-container">
                <h1 className="admin-type-match-handle-title">Gestisci TypeMatch</h1>
                <p className="admin-type-match-handle-description">
                    Qui puoi visualizzare i Type Match raggruppati e aggiungerne di nuovi.
                </p>

                <DivForButtonAggiungiTypeMatch className="admin-type-match-handle-button-aggiungi-type-match">
                    <button
                        className="btn-handle-aggiungi-type-match"
                        onClick={() => setIsModalOpen(true)}
                        disabled={isLoading || isLoadingTypes}
                    >
                        {isLoading || isLoadingTypes ? "Caricamento..." : "Aggiungi Type Match"}
                    </button>
                </DivForButtonAggiungiTypeMatch>

                {(!typeMatchesGrouped || groupedEntries.length === 0) && (
                    <InfoBox className="admin-type-match-handle-empty">
                        Nessun Type Match disponibile.{" "}
                        <button
                            type="button"
                            className="btn btn-link"
                            style={{ color: "gold", textDecoration: "underline" }}
                            onClick={fetchAllTypeMatches}
                        >
                            Ricarica
                        </button>
                    </InfoBox>
                )}

                {groupedEntries.length > 0 && (
                    <GroupsContainer className="admin-type-match-handle-groups-container">
                        {groupedEntries.map(([groupName, list]) => {
                            const sortedList = [...list as any[]].sort((a, b) =>
                                (a?.name || "").localeCompare(b?.name || "", "it")
                            );
                            return (
                                <GroupBox key={groupName as string} className="admin-type-match-handle-group-box">
                                    <GroupHeader className="admin-type-match-handle-group-header">
                                        <h2>{groupName as string}</h2>
                                        <span>{sortedList.length} elementi</span>
                                    </GroupHeader>
                                    <GroupBody className="admin-type-match-handle-group-body">
                                        {sortedList.map((tm) => (
                                            <TypeMatchRow key={tm.id} className="admin-type-match-handle-type-match-row">
                                                <TypeMatchName className="admin-type-match-handle-type-match-name">
                                                    {tm.name}
                                                </TypeMatchName>
                                                <TypeMatchMeta className="admin-type-match-handle-type-match-meta">
                                                    Teams: {tm.num_teams} | Min Wrestlers: {tm.min_num_wrestlers} | Max Wrestlers: {tm.max_num_wrestlers}
                                                </TypeMatchMeta>
                                            </TypeMatchRow>
                                        ))}
                                    </GroupBody>
                                </GroupBox>
                            );
                        })}
                    </GroupsContainer>
                )}

                {isModalOpen && (
                    <CreateTypeMatchModale
                        closeModalClick={() => setIsModalOpen(false)}
                        typeMatchTypes={typeMatchTypes}
                        onSave={handleCreateTypeMatch}
                    />
                )}
            </Container>
        </CommonWrapper>
    );
}

export default TypeMatchHandle;

